import { useCallback, useEffect, useState } from 'react';
import { useLogto } from '@logto/react';

import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { logout, setBridgeAuthenticated, setStatus, setUser } from '@/store/auth.slice';
import {
  clearStoredTokens,
  dbg,
  consumeAuthStartParam,
  getLogtoAccessToken,
  getRedirectUri,
  hasStoredAccessToken,
  hasStoredSession,
  isTelegramWebView,
  LAST_CONSUMED_AUTH_CODE,
  setAccessTokenGetter,
  setSessionExpiredHandler,
  startTelegramSignIn,
  isGrantDeclaredDead,
} from '@/lib/logto';
import { useGetMeQuery } from '@/services/endpoints/auth';
import { useGetLimuloMeQuery } from '@/services/limuloApi';
import { limuloMeToAuthUser } from '@/lib/limulo/identity';
import { getLogtoRolesFromAccessToken } from '@/lib/logto';
import { env } from '@/env';

/**
 * Drives sign-in in two contexts:
 *
 *  - **Standalone web** (browser tab, Telegram dev mock): standard Logto SDK
 *    redirect flow. `useHandleSignInCallback` on `/auth/callback` finishes it.
 *  - **Real Telegram WebView**: Logto's interactive flow can't run inside the
 *    embedded WebView (Google blocks OAuth there). Instead `startTelegramSignIn`
 *    opens the system browser via `WebApp.openLink`. The bridge in
 *    `<AuthCallback>` deep-links back via `t.me/.../app?startapp=auth_<code>`,
 *    and on relaunch `consumeAuthStartParam` exchanges the code for tokens.
 *
 * Auth state lives in Redux (`state.auth.bridgeAuthenticated`) so all
 * components that read `useLogtoAuth()` stay in sync — particularly important
 * for sign-out, where the trigger lives in <Profile>/<Settings> but the gate
 * lives in <AppShell>.
 *
 * Resume handling: when Telegram brings the existing Mini App back to
 * foreground via the t.me deep link (instead of fully re-launching it), no
 * component re-mounts, so a plain mount-time effect would miss the new
 * `auth_<code>` startParam. We re-run the consume on `visibilitychange` /
 * `focus` events and dedup by storing the last consumed code in localStorage.
 */
const LAST_CONSUMED_CODE = LAST_CONSUMED_AUTH_CODE;

export function useLogtoAuth() {
  const dispatch = useAppDispatch();
  const inTelegram = isTelegramWebView();

  const { isAuthenticated: sdkAuthenticated, isLoading, getAccessToken, signIn, signOut } =
    useLogto();

  const bridgeAuthenticated = useAppSelector((s) => s.auth.bridgeAuthenticated);
  const [bridgeChecked, setBridgeChecked] = useState(!inTelegram);

  useEffect(() => {
    if (!inTelegram) return;

    let cancelled = false;
    let running = false;

    const tryConsume = async (reason: string): Promise<void> => {
      if (running) return;
      if (hasStoredAccessToken()) {
        dispatch(setBridgeAuthenticated(true));
        setBridgeChecked(true);
        return;
      }
      running = true;
      try {
        // Stale access token but a persisted session (refresh_token present) →
        // silently refresh rather than bouncing to Login. Keeps the user signed
        // in across reboots/refreshes; only an explicit logout (which clears the
        // refresh_token) requires re-auth. If the refresh fails (dead token) we
        // clear the session and fall through to the sign-in path → clean Login,
        // not a wall of 401s.
        if (hasStoredSession()) {
          const refreshed = await getLogtoAccessToken();
          if (cancelled) return;
          if (refreshed) {
            dispatch(setBridgeAuthenticated(true));
            setBridgeChecked(true);
            return;
          }
          // 🔴 QUI SI PERDEVANO LE SESSIONI, E BASTAVA UN SOLO RINNOVO FALLITO.
          //
          // Fino al 06/09 questo ramo faceva `clearStoredTokens()` **incondizionato**. Ma
          // `getLogtoAccessToken()` torna `null` per QUALUNQUE motivo — inclusa una rete
          // che cade a meta' chiamata: `redeemOnce` intercetta l'errore e torna
          // `{accessToken: null}` (`logto.ts`, ramo `refresh network error`) **senza
          // cancellare niente**, ed era questa riga a cancellare tutto dopo.
          // ⇒ E `clearStoredTokens()` azzera anche `qlash.limulo.session`, che sta in
          // `PER_SERVICE_TOKEN_KEYS`: **un singolo `connectionfailed` su `oidc/token`
          // spazzava via identita' Logto E sessione Limulo insieme.**
          //
          // 📌 RIPRODOTTO, non dedotto (or45, 06/09 10:33Z): **una sola** chiamata
          // `POST oidc/token` abortita → `refresh_token`, `access_token` e
          // `qlash.limulo.session` tutti spariti, **zero `invalid_grant`**, e in
          // `bridge_debug.v2` la confessione di questa riga:
          // *«sessione cancellata: c era una sessione persistita ma il refresh non ha
          // reso un token»*.
          //
          // 🔑 LA CORREZIONE: il teardown di un grant DAVVERO morto lo possiede gia'
          // `handleDeadGrant` (che cancella **e** chiama `onSessionExpired`). Duplicarlo
          // qui era inutile nel caso terminale e distruttivo in quello transitorio.
          // ⇒ Si cancella **solo** se il grant e' stato dichiarato morto; altrimenti la
          // sessione RESTA e il prossimo tentativo puo' guarire da solo.
          //
          // ⚠️ Il verso in cui si sbaglia non e' simmetrico: tenere una sessione morta
          // costa una schermata di Login in piu'; buttarne una viva costa all'utente il
          // lavoro in corso e un login vero.
          if (isGrantDeclaredDead()) {
            dbg('sessione cancellata: il grant e stato dichiarato morto');
            clearStoredTokens();
          } else {
            dbg('nessun token da questo giro, ma il grant NON e dichiarato morto: la sessione resta');
          }
          dispatch(setBridgeAuthenticated(false));
        }
        // Peek startParam first to dedup repeat invocations on the same code.
        let startParam: string | undefined;
        try {
          const { retrieveLaunchParams } = await import('@tma.js/sdk-react');
          startParam = retrieveLaunchParams().tgWebAppStartParam;
        } catch {
          /* ignore */
        }
        const code = startParam?.startsWith('auth_') ? startParam.slice(5) : null;
        if (code && localStorage.getItem(LAST_CONSUMED_CODE) === code) {
          console.info('[auth] skipping already-consumed code (', reason, ')');
          setBridgeChecked(true);
          return;
        }
        console.info('[auth] attempting consume (', reason, ') startParam=', startParam?.slice(0, 24));
        const result = await consumeAuthStartParam();
        if (cancelled) return;
        if (result === true) {
          if (code) localStorage.setItem(LAST_CONSUMED_CODE, code);
          dispatch(setBridgeAuthenticated(true));
        } else {
          // Nothing restored a session (no fresh token, refresh failed/absent,
          // no sign-in code) → ensure the gate shows Login.
          dispatch(setBridgeAuthenticated(false));
        }
        setBridgeChecked(true);
      } catch (err) {
        // Anything thrown in here used to leave the gate stuck on "Loading…"
        // FOREVER: the finally only reset `running`, so `bridgeChecked` stayed
        // false and `effectiveLoading` never flipped. A refresh call that
        // rejects (network, Logto 5xx, a dead-grant handler that throws) was
        // enough. Observed 2026-07-26 from the Telegram app: "Loading…" and
        // nothing else, no error on screen because nothing rendered.
        //
        // We do NOT swallow it silently: log it, and let the finally below open
        // the gate to Login — a sign-in screen the user can act on beats a
        // spinner that never ends.
        console.error('[auth] consume failed (', reason, '):', err);
        if (!cancelled) dispatch(setBridgeAuthenticated(false));
      } finally {
        running = false;
        // The single guarantee that matters: whatever happened above — success,
        // early return, throw — the auth check is OVER. Every other path that
        // sets this is now redundant, and that is on purpose: this is the one
        // that cannot be forgotten by a future branch.
        if (!cancelled) setBridgeChecked(true);
      }
    };

    void tryConsume('mount');

    const onVisible = (): void => {
      if (document.visibilityState === 'visible') void tryConsume('visibilitychange');
    };
    const onFocus = (): void => void tryConsume('focus');
    document.addEventListener('visibilitychange', onVisible);
    window.addEventListener('focus', onFocus);

    return () => {
      cancelled = true;
      document.removeEventListener('visibilitychange', onVisible);
      window.removeEventListener('focus', onFocus);
    };
  }, [inTelegram, dispatch]);

  // Expose getAccessToken to non-React callers (RTK Query). The wrapper in
  // `lib/logto.ts` prefers our manually-stored token, so this getter is only
  // used in the standalone web fallback.
  useEffect(() => {
    setAccessTokenGetter(getAccessToken);
  }, [getAccessToken]);

  // When the shared refresh grant dies (terminal 400 invalid_grant on any
  // per-service redemption), logto.ts has already wiped the session — drop the
  // gate to a clean Login instead of leaving every section 401ing / masked as
  // the shared "Dev" user. This is the self-healing path for the recurring
  // account-switch dead-grant bug.
  useEffect(() => {
    setSessionExpiredHandler(() => {
      dispatch(logout());
    });
  }, [dispatch]);

  const isAuthenticated = bridgeAuthenticated || sdkAuthenticated;
  const effectiveLoading = inTelegram ? !bridgeChecked && !bridgeAuthenticated : isLoading;

  // Reflect loading state in Redux so common components can show spinners.
  useEffect(() => {
    dispatch(setStatus(effectiveLoading ? 'loading' : 'idle'));
  }, [effectiveLoading, dispatch]);

  // Pull the QLASH user record once authenticated — da Limulo o da Laravel, MAI da
  // entrambi.
  //
  // 🔑 Perche' e' una scelta di BUILD e non un fallback. Fino al 2026-09-05 questa riga
  // chiamava sempre il monolite Laravel (`GET /users/me`), dietro nessun flag: era
  // l'ultimo pezzo del boot che legava a Laravel anche una build in cui Laravel non
  // serve piu' (decisione di Luca 05/09: «l'app deve prendere solo dati Limulo e i suoi
  // nuovi servizi»). Il costo era misurabile, non teorico: con il monolite di staging
  // giu' (503 da API Gateway, misurato su `stg-tma` il 05/09) la sezione `/limulo/*`
  // mostrava dati veri mentre il boot bruciava ~19 s di rete per schermata su una
  // chiamata di cui non aveva bisogno.
  //
  // ⚠️ NON e' «prova Limulo, se fallisce ripiega su Laravel». Un ripiego qui sarebbe un
  // guasto d'identita': i due backend rispondono con DUE SPAZI DI ID diversi (`id`
  // numerico Laravel contro ObjectId stringa Limulo), e uno store popolato ora dall'uno
  // ora dall'altro darebbe a valle due chiavi di giunzione incompatibili senza che
  // nessuna schermata se ne accorga. La build sceglie una volta, all'accensione del
  // flag, e resta su quella.
  //
  // Le due query sono dichiarate entrambe (le hook di RTK Query non si chiamano dentro
  // un ramo) e si escludono a vicenda con `skip`: quella spenta non fa rete.
  const limuloOwnsIdentity = env.featureLimuloEnabled;
  const { data: laravelMe } = useGetMeQuery(undefined, {
    skip: !isAuthenticated || limuloOwnsIdentity,
  });
  const { data: limuloMe } = useGetLimuloMeQuery(undefined, {
    skip: !isAuthenticated || !limuloOwnsIdentity,
  });
  useEffect(() => {
    // I ruoli si leggono QUI e si passano come parametro: `limuloMeToAuthUser` e' pura
    // apposta, cosi' `tools/verify-limulo.ts` puo' esercitarla sotto Node.
    const next = limuloOwnsIdentity
      ? limuloMeToAuthUser(limuloMe, getLogtoRolesFromAccessToken())
      : laravelMe;
    if (next) dispatch(setUser(next));
  }, [limuloOwnsIdentity, limuloMe, laravelMe, dispatch]);

  const triggerSignIn = useCallback(() => {
    if (inTelegram) void startTelegramSignIn();
    else void signIn(getRedirectUri());
  }, [inTelegram, signIn]);

  const triggerSignOut = useCallback(() => {
    // Flip the Redux gate immediately so the UI drops to <Login /> right away.
    dispatch(logout());
    // Drop Logto SDK in-memory state. Calling `signOut()` without a post-logout
    // URI just clears tokens — no redirect — so it's safe inside the Telegram
    // WebView. The SDK mutates its OWN `logto:` localStorage keys here; we then
    // run `clearStoredTokens()` AFTER it settles so our wipe is the last writer
    // and no stale `logto:` entry (or in-flight refresh) survives. Without the
    // ordering, a late SDK write could re-seed a `logto:` key and the next
    // sign-in would short-circuit as "still authenticated" instead of doing a
    // full fresh authorization that mints a NEW refresh_token.
    void signOut()
      .catch(() => {
        /* SDK may throw if it had no session — ignore. */
      })
      .finally(() => {
        clearStoredTokens();
      });
    // Also clear synchronously up-front: if `signOut()` never resolves (older
    // SDK / no session), the bridge tokens are still gone immediately and the
    // AppShell gate (driven by Redux above) already shows <Login />.
    clearStoredTokens();
  }, [signOut, dispatch]);

  return {
    isAuthenticated,
    isLoading: effectiveLoading,
    signIn: triggerSignIn,
    signOut: triggerSignOut,
  };
}
